// The store header's entrance (variant `header`, judged in the ?store
// preview): when the store's own header shows over our section.
//   visible  always (the store's behaviour today: transparent at the top)
//   fade     hidden at the very top; fades in once the visitor scrolls, or,
//            with a fade point (option `headerat`, 0..100), once the
//            opening has gone that far of the way to upright: 0 is the
//            first scroll, 100 the same moment as late
//   late     hidden through the opening shot; fades in once the cross has
//            turned upright (the pose model's second keyframe)
// The fade point counts in the choreography's progress, not in pixels, so
// it is the same moment of the opening on every screen. Late stays a mode
// of its own, though it is fade at 100: it is a value the store's section
// may already have saved.
//
// Our section only says WHETHER the header is hidden, as a class on <html>
// (HIDDEN_CLASS); the CSS that hides it (index.html, "the store's header")
// is scoped to our product template, so nothing else in the theme changes.
// In the Shopify section this maps one to one: the section's settings pick
// the mode and the fade point (`header`, `headerat`), its script toggles
// the same class from the same scroll readings, and the same rule ships in
// the section's stylesheet, scoped by the template's body class
// (template-suffix-cross-usb). The theme's header itself is never edited.
// One difference: there the header is in the server-rendered HTML before
// any script runs, and our section comes after it, so the section can't
// hide it from the first paint: on some loads the browser paints the header
// first and it then fades out (a flash). Only the theme's <head> can: the
// package's snippets/nothing-cross-head.liquid, rendered there (one line of
// the install, shopify/INSTALL.md), hides it on our template and marks
// <html> (HEAD_LINES: data-nc-head, the package version). So in the theme
// fade and late take effect only with that mark; without it the header
// stays visible, no flash and no fade (storeHeaderMode), and the theme
// editor's check says why. Here the store chrome arrives after our script
// has set the class.
import { SITE_KEYFRAMES } from "../pose.js";

export const HEADER_MODES = ["visible", "fade", "late"];
export const HIDDEN_CLASS = "nc-store-header-hidden";
// the mark the theme's <head> lines leave on <html>: data-nc-head (its
// value the package version), read as dataset.ncHead
export const HEAD_LINES = "ncHead";
// the opening shot ends where the cross is upright (rotZ back to 0): the
// second keyframe, on phones too (SITE_KEYFRAMES_PHONE shares it)
export const LATE_AT = SITE_KEYFRAMES[1].p;
// fade's fade point (option headerat): percent of the way to upright
export const FADE_AT = Object.freeze({ min: 0, max: 100, step: 1, default: 0 });

// whether the store's header is hidden, given how far the page has scrolled
// (px) and the choreography's progress (0..1); `at`: fade's fade point
// (FADE_AT, percent of the way to LATE_AT)
export function headerHidden(mode, { scrollTop, progress }, at = FADE_AT.default) {
  if (!(Number.isInteger(at) && at >= FADE_AT.min && at <= FADE_AT.max)) {
    throw new RangeError(`header: the fade point must be a whole number ${FADE_AT.min}..${FADE_AT.max}, got ${at}`);
  }
  switch (mode) {
    case "visible": return false;
    case "fade": return scrollTop <= 0 || progress < (at / 100) * LATE_AT;
    case "late": return progress < LATE_AT;
    default: throw new Error(`Unknown header="${mode}"; expected one of ${HEADER_MODES.join(", ")}`);
  }
}

// The store's header is transparent on our template (the store's
// nr-custom.css: its text black, over the page), so over a dark picture
// scrolled under it (the scanning video's bottom, a gallery photo) its links,
// logo and cart can't be read. Over our sections after the hero it stands
// on a band of the page's backdrop (BAND_CLASS on <html>; css/theme.css's
// rule, the theme's section stylesheet, in the store and the ?store
// preview); over the hero (the cross's stage, the same backdrop) and the
// store's own sections below ours it stays transparent, as the store has it.
export const BAND_CLASS = "nc-store-header-band";

// whether the store's header stands on its band: px from the viewport's top
// of the hero's bottom edge and of our last section's, and the header's
// height (Horizon's --header-height; 0 without the store's header)
export function headerBand({ heroBottom, pageBottom, headerHeight }) {
  for (const [name, v] of Object.entries({ heroBottom, pageBottom, headerHeight })) {
    if (!Number.isFinite(v)) throw new TypeError(`header: ${name} must be a number, got ${v}`);
  }
  return headerHeight > 0 && heroBottom < headerHeight && pageBottom > 0;
}

// Over a dark section (the scanning video's: its black) the band would be a
// light strip across the dark; there the header is transparent instead, its
// links, cart and menu button white, the store's logo as it is. Not a
// switch: as the section's edge passes through the header's height the
// band fades out and the text goes from black to white in step with the
// scroll (headerDarkness, 0..1, as DARK_VAR on <html>; DARK_CLASS while it
// is above 0; css/theme.css), back the same way scrolling up, and in
// reverse at the section's bottom edge.
export const DARK_CLASS = "nc-store-header-dark";
export const DARK_VAR = "--nc-header-dark";

/** How much of the store's header stands over a dark section, 0..1 (the
 * share of its height, in whole px: a fraction of a px scrolled, as a
 * phone's screen scrolls, leaves no tint once the edge has passed):
 * `dark`, those sections' { top, bottom } px from the viewport's top;
 * `headerHeight`, as headerBand's. */
export function headerDarkness({ dark, headerHeight }) {
  if (!Number.isFinite(headerHeight)) throw new TypeError(`header: headerHeight must be a number, got ${headerHeight}`);
  if (headerHeight <= 0) return 0;
  const over = ({ top, bottom }) => Math.round(Math.min(bottom, headerHeight) - Math.max(top, 0));
  return Math.max(0, ...dark.map((s) => Math.min(1, Math.max(0, over(s) / headerHeight))));
}

// The store header's mode in the theme, given whether the theme's <head>
// lines ran (HEAD_LINES): the section's setting with them; without them
// "visible", whatever the setting (fading would flash, see above).
export function storeHeaderMode(mode, headLines) {
  if (!HEADER_MODES.includes(mode)) throw new Error(`Unknown header="${mode}"; expected one of ${HEADER_MODES.join(", ")}`);
  if (typeof headLines !== "boolean") throw new TypeError(`header: headLines must be true or false, got ${headLines}`);
  return headLines ? mode : "visible";
}
